import { z } from "zod";
import { BusinessDate } from "./common.js";
import { Id } from "./ids.js";

export const ProfitReportQuery = z.object({
  from: BusinessDate,
  to: BusinessDate,
  organizationId: Id.optional(),
});
export type ProfitReportQuery = z.infer<typeof ProfitReportQuery>;

export const ProfitRowStatus = z.enum(["ok", "missing_price", "missing_cost", "missing_both"]);
export type ProfitRowStatus = z.infer<typeof ProfitRowStatus>;

export const ProfitFoodRow = z.object({
  organizationId: Id,
  organizationName: z.string(),
  menuItemId: z.string(),
  foodName: z.string(),
  isSideItem: z.boolean(),
  isSpecialPortion: z.boolean().optional(),
  portions: z.number().int().nonnegative(),
  revenueRial: z.number().int().nullable(),
  costRial: z.number().int().nullable(),
  profitRial: z.number().int().nullable(),
  profitPerPortionRial: z.number().int().nullable(),
  marginPerMille: z.number().int().nullable(),
  status: ProfitRowStatus,
});
export type ProfitFoodRow = z.infer<typeof ProfitFoodRow>;

export const ProfitDayRow = z.object({
  businessDate: BusinessDate,
  portions: z.number().int().nonnegative(),
  revenueRial: z.number().int(),
  costRial: z.number().int(),
  grossProfitRial: z.number().int(),
  marginPerMille: z.number().int(),
  missingPriceCount: z.number().int().nonnegative(),
  missingCostCount: z.number().int().nonnegative(),
});
export type ProfitDayRow = z.infer<typeof ProfitDayRow>;

export const OrganizationProfitSummary = z.object({
  organizationId: Id,
  organizationName: z.string(),
  totalPortions: z.number().int().nonnegative(),
  totalRevenueRial: z.number().int(),
  totalCostRial: z.number().int(),
  totalProfitRial: z.number().int(),
  profitPerPortionRial: z.number().int(),
  marginPerMille: z.number().int(),
  specialPortionsCostRial: z.number().int().nonnegative(),
  hasMissingData: z.boolean(),
});
export type OrganizationProfitSummary = z.infer<typeof OrganizationProfitSummary>;

export const ProfitPeriodSummary = z.object({
  totalPortions: z.number().int().nonnegative(),
  totalRevenueRial: z.number().int(),
  totalCostRial: z.number().int(),
  totalProfitRial: z.number().int(),
  profitPerPortionRial: z.number().int(),
  marginPerMille: z.number().int(),
  specialPortionsCostRial: z.number().int().nonnegative(),
  hasMissingData: z.boolean(),
});
export type ProfitPeriodSummary = z.infer<typeof ProfitPeriodSummary>;

export const ProfitReportResponse = z.object({
  period: z.object({ from: BusinessDate, to: BusinessDate }),
  rows: z.array(ProfitFoodRow),
  days: z.array(ProfitDayRow),
  organizationSummaries: z.array(OrganizationProfitSummary),
  totalSummary: ProfitPeriodSummary,
});
export type ProfitReportResponse = z.infer<typeof ProfitReportResponse>;

/** Computes the start (1st) and end dates (in ISO YYYY-MM-DD) of the current Jalali month in Asia/Tehran. */
export function jalaliMonthRange(ref = new Date()): {
  startIso: BusinessDate;
  endIso: BusinessDate;
  jYear: number;
  jMonth: number;
} {
  const parts = new Intl.DateTimeFormat("en-US-u-ca-persian", {
    timeZone: "Asia/Tehran",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(ref);

  const jYear = Number(parts.find((p) => p.type === "year")?.value ?? "1405");
  const jMonth = Number(parts.find((p) => p.type === "month")?.value ?? "1");
  const jDay = Number(parts.find((p) => p.type === "day")?.value ?? "1");

  const start = new Date(ref.getTime() - (jDay - 1) * 86_400_000);
  const startIso = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tehran" }).format(start);
  const daysInMonth = jMonth <= 6 ? 31 : jMonth <= 11 ? 30 : 29;
  const end = new Date(start.getTime() + (daysInMonth - 1) * 86_400_000);
  const endIso = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tehran" }).format(end);

  return { startIso, endIso, jYear, jMonth };
}
